// src/contexts/WalletContext.jsx (Fixed version)
import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { 
    collection, 
    doc, 
    getDoc, 
    getDocs, 
    updateDoc, 
    addDoc, 
    query, 
    where, 
    orderBy, 
    Timestamp,
    increment,
    runTransaction,
    setDoc
} from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

const WalletContext = createContext();

export const useWallet = () => useContext(WalletContext);

export const WalletProvider = ({ children }) => {
    const { currentUser, userData, isAdmin } = useAuth(); // Add isAdmin here
    const { showToast } = useToast();
    const [wallet, setWallet] = useState(null);
    const [walletBalance, setWalletBalance] = useState(0);
    const [transactions, setTransactions] = useState([]);
    const [pledges, setPledges] = useState([]);
    const [loading, setLoading] = useState(true);

    // Wallet accounts
    const walletAccounts = [
        { id: 'main', name: 'Main Wallet', description: 'General purpose wallet' },
        { id: 'missions', name: 'Missions Account', description: 'For mission contributions' },
        { id: 'social_lunch', name: 'Social Lunch Account', description: 'Social lunch contributions' },
        { id: 'sabbath_fare', name: 'Sabbath Outing Fare', description: 'Sabbath transportation' },
        { id: 'contributions', name: 'General Contributions', description: 'General ministry contributions' },
        { id: 'development', name: 'Development Fund', description: 'Church development projects' }
    ];

    // Load user wallet data
    useEffect(() => {
        if (currentUser) {
            loadWallet();
            loadTransactions();
            loadPledges();
        }
    }, [currentUser]);

    const loadWallet = async () => {
        try {
            const walletRef = doc(db, 'wallets', currentUser.uid);
            const walletDoc = await getDoc(walletRef);
            
            if (walletDoc.exists()) {
                const walletData = walletDoc.data();
                setWallet(walletData);
                setWalletBalance(walletData.balance || 0);
            } else {
                // Create new wallet for user
                const newWallet = {
                    userId: currentUser.uid,
                    userName: currentUser?.fullName || userData?.firstName || 'User',
                    email: currentUser.email,
                    balance: 0,
                    accounts: {
                        main: 0,
                        missions: 0,
                        social_lunch: 0,
                        sabbath_fare: 0,
                        contributions: 0,
                        development: 0
                    },
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now()
                };
                await setDoc(walletRef, newWallet);
                setWallet(newWallet);
                setWalletBalance(0);
            }
        } catch (error) {
            console.error('Error loading wallet:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadTransactions = async () => {
        try {
            const transactionsRef = collection(db, 'walletTransactions');
            const q = query(
                transactionsRef,
                where('userId', '==', currentUser.uid),
                orderBy('createdAt', 'desc')
            );
            const snapshot = await getDocs(q);
            const transactionsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setTransactions(transactionsData);
        } catch (error) {
            console.error('Error loading transactions:', error);
        }
    };

    const loadPledges = async () => {
        try {
            const pledgesRef = collection(db, 'pledges');
            const q = query(
                pledgesRef,
                where('userId', '==', currentUser.uid),
                orderBy('createdAt', 'desc')
            );
            const snapshot = await getDocs(q);
            const pledgesData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setPledges(pledgesData);
        } catch (error) {
            console.error('Error loading pledges:', error);
        }
    };

    // Initiate M-Pesa STK Push
    const initiateMpesaPayment = async (phoneNumber, amount, accountId, accountName, description) => {
        try {
            const response = await fetch('/.netlify/functions/mpesa-stkpush', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    phoneNumber,
                    amount,
                    accountId,
                    accountName,
                    description,
                    userId: currentUser.uid,
                    userName: userData?.fullName || currentUser.email
                })
            });

            const result = await response.json();
            
            if (result.success) {
                showToast('STK Push sent to your phone. Please enter PIN to complete payment.', 'info');
                return { success: true, checkoutRequestID: result.checkoutRequestID };
            } else {
                showToast(result.error || 'Payment initiation failed', 'error');
                return { success: false, error: result.error };
            }
        } catch (error) {
            console.error('Error initiating payment:', error);
            showToast('Failed to initiate payment', 'error');
            return { success: false, error: error.message };
        }
    };

    // Check M-Pesa payment status
    const checkPaymentStatus = async (checkoutRequestID) => {
        try {
            const response = await fetch('/.netlify/functions/mpesa-status', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ checkoutRequestID })
            });
            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Error checking payment status:', error);
            return { success: false };
        }
    };

    // Add funds to wallet (for admin or manual entry)
    const addFunds = async (amount, accountId, accountName, description, paymentMethod = 'manual') => {
        try {
            const walletRef = doc(db, 'wallets', currentUser.uid);
            
            await runTransaction(db, async (transaction) => {
                const walletDoc = await transaction.get(walletRef);
                if (!walletDoc.exists()) {
                    throw new Error('Wallet not found');
                }

                const currentBalance = walletDoc.data().balance || 0;
                const currentAccountBalance = walletDoc.data().accounts?.[accountId] || 0;

                transaction.update(walletRef, {
                    balance: currentBalance + amount,
                    [`accounts.${accountId}`]: currentAccountBalance + amount,
                    updatedAt: Timestamp.now()
                });

                const transactionRef = collection(db, 'walletTransactions');
                transaction.set(doc(transactionRef), {
                    userId: currentUser.uid,
                    userName: userData?.fullName || currentUser.email,
                    type: 'deposit',
                    amount: amount,
                    accountId: accountId,
                    accountName: accountName,
                    description: description,
                    paymentMethod: paymentMethod,
                    status: 'completed',
                    reference: `DEP-${Date.now()}`,
                    createdAt: Timestamp.now(),
                    createdBy: currentUser.uid
                });
            });

            showToast(`KES ${amount.toLocaleString()} added to ${accountName} successfully!`, 'success');
            await loadWallet();
            await loadTransactions();
            return { success: true };
        } catch (error) {
            console.error('Error adding funds:', error);
            showToast('Failed to add funds', 'error');
            return { success: false };
        }
    };

    // Make a payment from wallet
    const makePayment = async (amount, accountId, accountName, description, category) => {
        if (amount > walletBalance) {
            showToast('Insufficient wallet balance', 'error');
            return { success: false, error: 'Insufficient balance' };
        }

        try {
            const walletRef = doc(db, 'wallets', currentUser.uid);
            
            await runTransaction(db, async (transaction) => {
                const walletDoc = await transaction.get(walletRef);
                if (!walletDoc.exists()) {
                    throw new Error('Wallet not found');
                }

                const currentBalance = walletDoc.data().balance || 0;
                const currentAccountBalance = walletDoc.data().accounts?.[accountId] || 0;

                if (currentBalance < amount) {
                    throw new Error('Insufficient balance');
                }

                transaction.update(walletRef, {
                    balance: currentBalance - amount,
                    [`accounts.${accountId}`]: currentAccountBalance - amount,
                    updatedAt: Timestamp.now()
                });

                const transactionRef = collection(db, 'walletTransactions');
                transaction.set(doc(transactionRef), {
                    userId: currentUser.uid,
                    userName: userData?.fullName || currentUser.email,
                    type: 'spending',
                    amount: amount,
                    accountId: accountId,
                    accountName: accountName,
                    description: description,
                    category: category,
                    status: 'completed',
                    reference: `PAY-${Date.now()}`,
                    createdAt: Timestamp.now(),
                    createdBy: currentUser.uid
                });

                const mainTransactionRef = collection(db, 'transactions');
                transaction.set(doc(mainTransactionRef), {
                    date: new Date().toISOString().split('T')[0],
                    description: `Wallet payment: ${description}`,
                    department: accountId,
                    reason: category,
                    type: 'expense',
                    amount: amount,
                    notes: `Payment from ${userData?.fullName}'s wallet`,
                    recordedBy: currentUser.uid,
                    recordedByName: userData?.fullName || currentUser.email,
                    recordedAt: Timestamp.now(),
                    sourceId: 'wallet',
                    sourceName: `${userData?.fullName}'s Wallet`,
                    updatedAt: Timestamp.now(),
                    updatedBy: currentUser.uid,
                    updatedByName: userData?.fullName || currentUser.email
                });
            });

            showToast(`KES ${amount.toLocaleString()} paid successfully!`, 'success');
            await loadWallet();
            await loadTransactions();
            return { success: true };
        } catch (error) {
            console.error('Error making payment:', error);
            showToast('Failed to process payment', 'error');
            return { success: false };
        }
    };

    // Create a pledge
    const createPledge = async (amount, reason, description, dueDate) => {
        try {
            const pledgeData = {
                userId: currentUser.uid,
                userName: userData?.fullName || currentUser.email,
                amount: amount,
                paidAmount: 0,
                remainingAmount: amount,
                reason: reason,
                description: description,
                dueDate: dueDate,
                status: 'pending',
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            };

            const docRef = await addDoc(collection(db, 'pledges'), pledgeData);
            showToast('Pledge created successfully!', 'success');
            await loadPledges();
            return { success: true, id: docRef.id };
        } catch (error) {
            console.error('Error creating pledge:', error);
            showToast('Failed to create pledge', 'error');
            return { success: false };
        }
    };

    // Pay pledge
    const payPledge = async (pledgeId, amount) => {
        try {
            const pledgeRef = doc(db, 'pledges', pledgeId);
            const pledgeDoc = await getDoc(pledgeRef);
            
            if (!pledgeDoc.exists()) {
                throw new Error('Pledge not found');
            }

            const pledge = pledgeDoc.data();
            const newPaidAmount = (pledge.paidAmount || 0) + amount;
            const newRemainingAmount = pledge.amount - newPaidAmount;
            const status = newRemainingAmount <= 0 ? 'completed' : 'partial';

            await updateDoc(pledgeRef, {
                paidAmount: newPaidAmount,
                remainingAmount: newRemainingAmount,
                status: status,
                updatedAt: Timestamp.now()
            });

            showToast(`KES ${amount.toLocaleString()} paid towards pledge!`, 'success');
            await loadPledges();
            return { success: true };
        } catch (error) {
            console.error('Error paying pledge:', error);
            showToast('Failed to process pledge payment', 'error');
            return { success: false };
        }
    };

    // Get wallet summary for admin
    const getWalletSummary = async () => {
        if (!isAdmin) return null;
        
        try {
            const walletsRef = collection(db, 'wallets');
            const snapshot = await getDocs(walletsRef);
            const wallets = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            
            const totalBalance = wallets.reduce((sum, w) => sum + (w.balance || 0), 0);
            const accountTotals = {};
            
            walletAccounts.forEach(account => {
                accountTotals[account.id] = wallets.reduce((sum, w) => sum + (w.accounts?.[account.id] || 0), 0);
            });
            
            return { wallets, totalBalance, accountTotals };
        } catch (error) {
            console.error('Error getting wallet summary:', error);
            return null;
        }
    };

    // Admin adjust wallet balance
    const adminAdjustBalance = async (userId, amount, accountId, reason) => {
        if (!isAdmin) {
            showToast('Unauthorized', 'error');
            return { success: false };
        }

        try {
            const walletRef = doc(db, 'wallets', userId);
            
            await runTransaction(db, async (transaction) => {
                const walletDoc = await transaction.get(walletRef);
                if (!walletDoc.exists()) {
                    throw new Error('Wallet not found');
                }

                const currentBalance = walletDoc.data().balance || 0;
                const currentAccountBalance = walletDoc.data().accounts?.[accountId] || 0;

                transaction.update(walletRef, {
                    balance: currentBalance + amount,
                    [`accounts.${accountId}`]: currentAccountBalance + amount,
                    updatedAt: Timestamp.now(),
                    lastAdminAdjustment: {
                        amount: amount,
                        reason: reason,
                        adjustedBy: currentUser.uid,
                        adjustedByName: userData?.fullName || currentUser.email,
                        adjustedAt: Timestamp.now()
                    }
                });
            });

            showToast(`Wallet balance adjusted by KES ${amount.toLocaleString()}`, 'success');
            return { success: true };
        } catch (error) {
            console.error('Error adjusting balance:', error);
            showToast('Failed to adjust balance', 'error');
            return { success: false };
        }
    };

    return (
        <WalletContext.Provider value={{
            wallet,
            walletBalance,
            transactions,
            pledges,
            loading,
            walletAccounts,
            initiateMpesaPayment,
            checkPaymentStatus,
            addFunds,
            makePayment,
            createPledge,
            payPledge,
            loadWallet,
            loadTransactions,
            loadPledges,
            getWalletSummary,
            adminAdjustBalance
        }}>
            {children}
        </WalletContext.Provider>
    );
};
